import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';

export interface EditingFieldInfo {
    fieldId: string;
    label: string;
    elementRef: React.RefObject<HTMLElement>;
}

interface HtmlFieldEditingContextType {
    registerField: (
        fieldId: string,
        label: string,
        elementRef: React.RefObject<HTMLElement>,
    ) => void;
    unregisterField: (fieldId: string) => void;
    setFieldEditing: (fieldId: string, isEditing: boolean) => void;
    hasEditingFields: () => boolean;
    getEditingFields: () => EditingFieldInfo[];
    scrollToField: (fieldId: string) => void;
}

const HtmlFieldEditingContext = createContext<HtmlFieldEditingContextType | null>(null);

export const HtmlFieldEditingProvider = ({ children }: { children: ReactNode }) => {
    const [editingFields, setEditingFields] = useState<Set<string>>(new Set());
    const fieldsMapRef = useRef<Map<string, EditingFieldInfo>>(new Map());

    const registerField = useCallback(
        (fieldId: string, label: string, elementRef: React.RefObject<HTMLElement>) => {
            fieldsMapRef.current.set(fieldId, { fieldId, label, elementRef });
        },
        [],
    );

    const unregisterField = useCallback((fieldId: string) => {
        fieldsMapRef.current.delete(fieldId);
        setEditingFields(prev => {
            const next = new Set(prev);
            next.delete(fieldId);
            return next;
        });
    }, []);

    const setFieldEditing = useCallback((fieldId: string, isEditing: boolean) => {
        setEditingFields(prev => {
            const next = new Set(prev);
            if (isEditing) {
                next.add(fieldId);
            } else {
                next.delete(fieldId);
            }
            return next;
        });
    }, []);

    const hasEditingFields = useCallback(() => editingFields.size > 0, [editingFields]);

    const getEditingFields = useCallback(
        () =>
            Array.from(editingFields)
                .map(fieldId => fieldsMapRef.current.get(fieldId))
                .filter((field): field is EditingFieldInfo => field !== undefined),
        [editingFields],
    );

    const scrollToField = useCallback((fieldId: string) => {
        const fieldInfo = fieldsMapRef.current.get(fieldId);
        if (fieldInfo?.elementRef.current) {
            fieldInfo.elementRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, []);

    const contextValue = useMemo(
        () => ({
            registerField,
            unregisterField,
            setFieldEditing,
            hasEditingFields,
            getEditingFields,
            scrollToField,
        }),
        [
            registerField,
            unregisterField,
            setFieldEditing,
            hasEditingFields,
            getEditingFields,
            scrollToField,
        ],
    );

    return (
        <HtmlFieldEditingContext.Provider value={contextValue}>
            {children}
        </HtmlFieldEditingContext.Provider>
    );
};

export const useHtmlFieldEditing = () => {
    const context = useContext(HtmlFieldEditingContext);
    if (!context) {
        // Return a no-op implementation if context is not available
        return {
            registerField: () => {},
            unregisterField: () => {},
            setFieldEditing: () => {},
            hasEditingFields: () => false,
            getEditingFields: () => [],
            scrollToField: () => {},
        };
    }
    return context;
};
